/**
 * Query keys that are not filters.
 *
 * Every list control writes to the query, so the query holds more than what
 * narrows the list: where one is, how it is sorted, which columns show. The
 * head counts the filters and the chip row names them, and both have to leave
 * out the same keys — counted apart, the column chooser alone made the list
 * look filtered (#2209).
 */
export const PALO_NON_FILTER_KEYS = [
    'sort',
    'order',
    'search',
    'limit',
    'page',
    'pageSize',
    'currency',
    'locale',
    // The sub-view of a list, set by the navigation rather than by a filter.
    'invoiceNumberSequenceId',
    // The column choice and its order ride the query so that a saved view
    // restores them; they are display settings, not filters.
    'columns',
    'columnsOn',
    'columnOrder',
];

/** The filters the query currently holds, in the order they stand in it. */
export const paloActiveFilters = (filter: URLSearchParams): [string, string][] =>
    Array.from(filter.entries()).filter(([key]) => !PALO_NON_FILTER_KEYS.includes(key));

/**
 * The query with every filter taken out.
 *
 * What page one is on and how one sorted are not something "clear the filters"
 * should take away.
 */
export const paloWithoutFilters = (filter: URLSearchParams): URLSearchParams => {
    const next = new URLSearchParams();

    Array.from(filter.entries())
        .filter(([key]) => PALO_NON_FILTER_KEYS.includes(key))
        .forEach(([key, value]) => next.append(key, value));

    return next;
};

/**
 * What each comparison is called.
 *
 * The two a field implies — `ist` for most, `enthält` for text — are left
 * unsaid on a chip, the way nobody says "Status ist Erfasst" out loud.
 */
export const PALO_OPERATOR_LABEL: Record<string, string> = {
    eq: 'ist',
    neq: 'ist nicht',
    contains: 'enthält',
    starts: 'beginnt mit',
    in: 'ist eine von',
    from: 'ab',
    until: 'bis',
    gt: 'größer als',
    lt: 'kleiner als',
    between: 'zwischen',
    empty: 'ist leer',
    notEmpty: 'ist gefüllt',
};

export interface PaloFilterParam {
    /** The whole parameter, as it stands in the address. */
    param: string;
    value: string;
    /** The field it names, without its brackets. */
    key: string;
    /** The comparison, where the parameter names one. */
    operator?: string;
}

/**
 * The filters an address holds, read out of their brackets.
 *
 * `filter[createdAt][from]=2026-06-01` is the field `createdAt` compared with
 * `from`; `filter[status]=X` and a plain `status=X` are the field alone. Each
 * keeps the parameter it came from, so removing one takes away exactly that
 * comparison and leaves the others as they are.
 */
export const paloFilterParams = (params: URLSearchParams): PaloFilterParam[] =>
    Array.from(params.entries())
        .map(([param, value]): PaloFilterParam => {
            if (!param.startsWith('filter[')) {
                return { param, value, key: param };
            }

            const close = param.indexOf(']', 7);
            const rest = param.substring(close + 1);
            const operator = rest.startsWith('[') ? rest.substring(1, rest.length - 1) : '';

            return { param, value, key: param.substring(7, close), operator: operator || undefined };
        })
        .filter(entry => !PALO_NON_FILTER_KEYS.includes(entry.key));
